import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DeviceViewer,
  type DeviceViewerHandle,
  type StreamParams,
  type DataChannelMessage,
  type DeviceInfoMessage,
} from "@beeos/device-viewer";
import {
  useDesktopViewer,
  type DesktopStreamParams,
  type RefreshStreamParamsFn,
} from "@beeos/desktop-viewer";
import {
  ViewerOverlay,
  ViewerSpinner,
  ViewerPrimaryButton,
  RefreshCwIcon,
  WifiOffIcon,
  AlertCircleIcon,
  ScreenShareTakeoverIcon,
} from "./components/ViewerOverlay";
import { isTrustedParentMessage, parentOriginFromReferrer } from "./parent-origin";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "";

/**
 * postMessage round-trip timeout for the parent-mediated
 * `refreshStreamParams` request. 8 s is comfortably > the worst-case
 * dashboard `/api/v1/instances/{id}/stream-url` round-trip (~2 s p99)
 * and short enough that a non-listening or unreachable parent fails
 * cleanly instead of hanging the viewer at "正在协商" forever.
 */
const PARENT_REFRESH_TIMEOUT_MS = 8_000;

/**
 * While the viewer is stranded in the terminal "lost" state (the
 * `WebRTCClient` gave up after MAX_RECONNECT_ATTEMPTS), auto-retry on
 * this cadence instead of stranding the user on a manual "Reconnect"
 * button. The device routinely comes back on its own — cable replug,
 * instance restart, or the device-agent recycling scrcpy after the host
 * resumes from sleep — so a hands-off retry is the right default. We also
 * retry immediately on tab-visible / network-online; the timer covers the
 * "user is staring at a stuck panel on a healthy network" case those
 * events never fire for. Kept above the ~10s answer-timeout so each
 * remount gets a fair shot before the next nudge.
 */
const LOST_AUTO_RECONNECT_INTERVAL_MS = 15_000;

function isLocalViewerHostname(hostname: string): boolean {
  const normalized = hostname.trim().toLowerCase().replace(/^\[|\]$/g, "");
  if (normalized === "localhost" || normalized === "127.0.0.1" || normalized === "::1") return true;
  if (normalized.startsWith("10.") || normalized.startsWith("192.168.")) return true;
  const match = /^172\.(\d{1,3})\./.exec(normalized);
  return Boolean(match && Number(match[1]) >= 16 && Number(match[1]) <= 31);
}

/**
 * Refresh streamParams by asking the same-origin parent window to
 * fetch them. The parent (e.g. dashboard's `WebRTCDesktopStream`)
 * already holds the user's JWT cookie / session, so it can call
 * `/api/v1/instances/{id}/stream-url` for us — the iframe doesn't
 * need its own `Authorization` header that way.
 *
 * Protocol matches `ViewerInbound/OutboundMessage` from
 * `@beeos/desktop-viewer/types`. We correlate request and response
 * via a UUID in case multiple refresh requests overlap (shouldn't
 * happen under single-flight, but be defensive).
 *
 * Rejects on:
 *   - timeout (parent didn't respond within 8 s)
 *   - parent's `refreshStreamParams.failed` reply
 *   - no parent at all (`window.parent === window`) — caller should
 *     gate this branch and provide a different refresher (or none)
 *     when running standalone.
 */
function refreshStreamParamsViaParent(): Promise<DesktopStreamParams> {
  if (window.parent === window) {
    return Promise.reject(new Error("no parent window — cannot delegate refresh"));
  }
  const parentOrigin = parentOriginFromReferrer(document.referrer);
  if (!parentOrigin) {
    return Promise.reject(new Error("parent_origin_unavailable"));
  }
  const requestId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `r-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      window.removeEventListener("message", handler);
      reject(new Error("parent_refresh_timeout"));
    }, PARENT_REFRESH_TIMEOUT_MS);
    function handler(e: MessageEvent) {
      if (!isTrustedParentMessage(e, window.parent, parentOrigin)) return;
      const m = e.data;
      if (!m || typeof m !== "object" || typeof m.type !== "string") return;
      if (m.requestId !== requestId) return;
      if (m.type === "beeos.viewer.refreshStreamParams.response") {
        clearTimeout(timeout);
        window.removeEventListener("message", handler);
        resolve(m.streamParams as DesktopStreamParams);
        return;
      }
      if (m.type === "beeos.viewer.refreshStreamParams.failed") {
        clearTimeout(timeout);
        window.removeEventListener("message", handler);
        reject(new Error(typeof m.reason === "string" ? m.reason : "parent_refresh_failed"));
      }
    }
    window.addEventListener("message", handler);
    window.parent.postMessage(
      { type: "beeos.viewer.refreshStreamParams.request", requestId },
      parentOrigin,
    );
  });
}

/** Map a backend `StreamParams` JSON onto the `DesktopStreamParams` shape. */
function toDesktopStreamParams(streamParams: StreamParams): DesktopStreamParams {
  return {
    mqttUrl: streamParams.mqttUrl,
    mqttToken: (streamParams as { mqttToken?: string }).mqttToken ?? "",
    deviceTopic: streamParams.deviceTopic,
    iceServers: ((streamParams.iceServers ?? []) as Array<{
      urls: string | string[];
      username?: string;
      credential?: string;
    }>).map((s) => ({
      urls: s.urls,
      username: s.username,
      credential: s.credential,
    })),
    vncPassword: (streamParams as { vncPassword?: string }).vncPassword,
    vncUsername: (streamParams as { vncUsername?: string }).vncUsername,
    expiresAt: (streamParams as { expiresAt?: number }).expiresAt,
  };
}

type ViewMode = "device" | "desktop";

/**
 * Transport-level error categories surfaced to the user. Determines
 * tone / icon / whether a "Reconnect" CTA is offered.
 *   - "kicked" : another viewer took over (most-recent-wins). The user
 *                CAN reconnect to take control back; doing so will
 *                kick the new viewer in turn (P2P symmetric semantics).
 *   - "lost"   : transport gave up after `MAX_RECONNECT_ATTEMPTS`.
 *                Reconnecting starts a fresh attempt cycle.
 *   - "fatal"  : auth / config error the iframe cannot recover from
 *                on its own (missing params, expired/invalid token).
 *                No reconnect button.
 */
type ErrorKind = "kicked" | "lost" | "fatal";

type AppState =
  | { phase: "error"; kind: ErrorKind; message?: string; streamParams?: StreamParams }
  | { phase: "loading" }
  | { phase: "ready"; streamParams: StreamParams };

interface ParsedRoute {
  instanceId: string | null;
  token: string | null;
  embed: boolean;
  mode: ViewMode;
  streamParams: StreamParams | null;
}

/** Notify parent window of status changes (only when embedded). */
function postStatus(type: string, detail?: Record<string, unknown>) {
  if (window.parent === window) return;
  const parentOrigin = parentOriginFromReferrer(document.referrer);
  if (!parentOrigin) return;
  window.parent.postMessage({ source: "device-viewer", type, ...detail }, parentOrigin);
}

function decodeBase64Url(s: string): string {
  let b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4) b64 += "=";
  return atob(b64);
}

function getQueryParam(name: string): string | null {
  const search = window.location.search;
  const prefix = `${name}=`;
  const idx = search.indexOf(prefix);
  if (idx < 0) return null;
  const start = idx + prefix.length;
  const end = search.indexOf("&", start);
  return decodeURIComponent(end < 0 ? search.slice(start) : search.slice(start, end));
}

function parseRoute(): ParsedRoute {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  const embed = params.get("embed") === "true";
  const mode: ViewMode = params.get("mode") === "desktop" ? "desktop" : "device";

  // Extract streamParams from raw URL to avoid URLSearchParams mangling base64 `+` chars
  const streamParamsRaw = getQueryParam("streamParams");

  let streamParams: StreamParams | null = null;
  if (streamParamsRaw) {
    try {
      streamParams = JSON.parse(decodeBase64Url(streamParamsRaw)) as StreamParams;
    } catch (e) {
      console.error("[parseRoute] Failed to decode streamParams:", e);
    }
  }

  const parts = window.location.pathname.split("/").filter(Boolean);
  const viewIdx = parts.indexOf("view");
  const instanceId = viewIdx >= 0 ? parts[viewIdx + 1] ?? null : parts[0] ?? null;

  return { instanceId, token, embed, mode, streamParams };
}

async function fetchStreamParams(
  instanceId: string,
  token: string,
  t: (key: string, vars?: Record<string, unknown>) => string,
): Promise<StreamParams> {
  const resp = await fetch(`${API_BASE}/api/v1/instances/${instanceId}/stream-url`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    const body = await resp.text().catch(() => "");
    throw new Error(
      resp.status === 401
        ? t("viewer.unauthorized")
        : t("viewer.fetchFailed", { status: resp.status, body }),
    );
  }
  const json = await resp.json();
  if (json.data) return json.data as StreamParams;
  return json as StreamParams;
}

export function App() {
  const { t } = useTranslation();
  const [state, setState] = useState<AppState>({ phase: "loading" });
  const [connected, setConnected] = useState(false);
  const [embed, setEmbed] = useState(false);
  const [mode, setMode] = useState<ViewMode>("device");
  /**
   * Bumping this remounts `<DeviceViewer>` so a brand-new
   * `WebRTCClient` is constructed under the same `viewerId`
   * (sessionStorage-backed). That re-publishes an offer on the same
   * MQTT topic and triggers scrcpy-bridge's most-recent-wins kick
   * against whoever took over us. No network request needed — the
   * cached `streamParams.mqttToken` is fresh enough on the realistic
   * "user clicked reconnect within seconds of being kicked" timeline.
   */
  const [remountKey, setRemountKey] = useState(0);
  /**
   * Source-of-truth for streamParams refresh:
   *   - "url-streamParams" → use parent postMessage (only works
   *     when iframe'd inside dashboard; when standalone we degrade)
   *   - "url-token"        → call /stream-url ourselves with the
   *     `Bearer ${token}` we already have
   *   - null               → no refresh available
   * Stored separately from `state` so the desktop branch can build
   * its `refreshStreamParams` closure without depending on AppState.
   */
  const [refreshSource, setRefreshSource] = useState<{
    kind: "url-streamParams";
  } | {
    kind: "url-token";
    instanceId: string;
    token: string;
  } | null>(null);
  const [deviceInfo, setDeviceInfo] = useState<{
    name: string;
    os: string;
    width: number;
    height: number;
  } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  // Local-webcam-into-device-camera sharing. Reflects the bridge's
  // `camera_status` acknowledgement, not just the local intent, so the
  // toggle only shows "on" once injection actually started.
  const [cameraSharing, setCameraSharing] = useState(false);
  const [cameraBusy, setCameraBusy] = useState(false);
  const viewerRef = useRef<DeviceViewerHandle>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // ── Auto camera-share driven by the device (`camera_needed` events) ──
  // The bridge tells us when an in-guest app opens/closes the camera; we
  // auto-start/stop webcam capture (AgentBay-style, no manual click).
  // These refs gate the automation per in-use edge:
  //  - cameraDeniedRef:  getUserMedia was denied — don't re-prompt until the
  //    app closes and reopens the camera (a fresh true edge).
  //  - cameraManualStopRef: the user explicitly stopped sharing — respect
  //    that intent for the rest of this edge (incl. bridge replays).
  //  - cameraStopDebounceRef: pending debounced stop; camera apps switching
  //    lenses emit close→open within ~1s, and tearing capture down in that
  //    window makes the webcam LED flicker.
  const cameraDeniedRef = useRef(false);
  const cameraManualStopRef = useRef(false);
  const cameraStopDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const CAMERA_STOP_DEBOUNCE_MS = 1500;

  useEffect(() => {
    return () => {
      if (cameraStopDebounceRef.current) clearTimeout(cameraStopDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    const route = parseRoute();
    setEmbed(route.embed);
    setMode(route.mode);

    if (route.streamParams) {
      setState({ phase: "ready", streamParams: route.streamParams });
      setRefreshSource({ kind: "url-streamParams" });
      return;
    }

    if (!route.instanceId || !route.token) {
      setState({
        phase: "error",
        kind: "fatal",
        message: t("viewer.missingParams"),
      });
      return;
    }

    setRefreshSource({ kind: "url-token", instanceId: route.instanceId, token: route.token });
    fetchStreamParams(route.instanceId, route.token, t)
      .then((streamParams) => setState({ phase: "ready", streamParams }))
      .catch((err) => setState({ phase: "error", kind: "fatal", message: err.message }));
  }, [t]);

  /**
   * Pick the right `refreshStreamParams` callback based on how the
   * page was opened. Memoized so a stable identity flows through to
   * the `useDesktopViewer` hook (it stashes the latest in a ref, but
   * keeping the ref pointing at one closure for the whole session
   * keeps debugging simpler).
   *
   * Returns `undefined` when:
   *   - `url-streamParams` source AND we're standalone (no parent to
   *     ask) → viewer falls through to its existing failed-state UX
   *     and the user must reopen from dashboard.
   */
  const desktopRefresher = useMemo<RefreshStreamParamsFn | undefined>(() => {
    if (!refreshSource) return undefined;
    if (refreshSource.kind === "url-token") {
      const { instanceId, token } = refreshSource;
      return async () => {
        const fresh = await fetchStreamParams(instanceId, token, t);
        return toDesktopStreamParams(fresh);
      };
    }
    if (refreshSource.kind === "url-streamParams") {
      if (window.parent === window) {
        return undefined;
      }
      return refreshStreamParamsViaParent;
    }
    return undefined;
  }, [refreshSource, t]);

  const handleConnected = useCallback(() => {
    setConnected(true);
    postStatus("connected");
  }, []);

  const handleDisconnected = useCallback((reason: string) => {
    setConnected(false);
    postStatus("disconnected", { reason });
    if (reason === "viewer_kicked") {
      // Carry `streamParams` from the live `ready` state into the
      // error state so handleReconnect can replay them without a
      // fresh network round-trip.
      setState((prev) =>
        prev.phase === "ready"
          ? { phase: "error", kind: "kicked", streamParams: prev.streamParams }
          : prev,
      );
      postStatus("kicked", { canReconnect: true });
      return;
    }
    if (reason !== "client disconnected" && reason !== "max reconnect attempts reached") {
      return;
    }
    if (reason === "max reconnect attempts reached") {
      setState((prev) =>
        prev.phase === "ready"
          ? { phase: "error", kind: "lost", streamParams: prev.streamParams }
          : prev,
      );
      postStatus("connection-lost", { canReconnect: true });
    }
  }, []);

  const handleReconnecting = useCallback((attempt: number) => {
    setConnected(false);
    postStatus("reconnecting", { attempt });
  }, []);

  /**
   * iframe-internal reconnect: rebuilds the WebRTC pipeline under the
   * same `viewerId` (sessionStorage-pinned) using the `streamParams`
   * cached on the error state. Bumping `remountKey` is what forces
   * `<DeviceViewer>` to dispose its `WebRTCClient` and create a fresh
   * one — calling `connect()` directly on the old (kicked + disposed)
   * client would no-op because `disposed=true` is sticky.
   *
   * No network request. If `streamParams.mqttToken` happens to be
   * expired by the time the user clicks (>10 min idle), the new
   * client's exponential-backoff path will exercise the
   * `refreshStreamParams` hook we wire on the device branch (same one
   * the desktop branch uses) — fully iframe-internal in URL-token
   * mode, falls through harmlessly in URL-streamParams + standalone
   * mode (no parent to ask).
   */
  const handleReconnect = useCallback(() => {
    const reconnect = async () => {
      const cached = state.phase === "error" ? state.streamParams : undefined;
      if (!cached) return;

      // A takeover can happen long after the iframe was opened. Refresh
      // before constructing the replacement client so it never enters an
      // EMQX "Bad User Name or Password" loop with the cached MQTT token.
      let next = cached;
      if (desktopRefresher) {
        try {
          next = (await desktopRefresher()) as unknown as StreamParams;
        } catch (error) {
          console.warn("[App] stream params refresh before reconnect failed:", error);
        }
      }
      setState({ phase: "ready", streamParams: next });
      setRemountKey((key) => key + 1);
      postStatus("reconnect-requested");
    };
    void reconnect();
  }, [desktopRefresher, state]);

  // Auto-recover from the terminal "lost" state. The WebRTCClient gives up
  // after MAX_RECONNECT_ATTEMPTS, but the device routinely returns (cable
  // replug, instance restart, or the device-agent recycling scrcpy after
  // the host resumes from sleep). Rather than stranding the user on a
  // manual "Reconnect", retry automatically: immediately when the tab
  // regains visibility or the network comes back, and on a steady timer
  // while we stay lost. `handleReconnect` rebuilds the pipeline and
  // refreshes the MQTT JWT via the parent, so each attempt is a clean shot.
  // `kicked` / `fatal` are intentionally excluded — those need a deliberate
  // user choice (take over) or can't self-heal (bad token / missing params).
  useEffect(() => {
    if (state.phase !== "error" || state.kind !== "lost" || !state.streamParams) {
      return;
    }
    const onRetry = () => handleReconnect();
    const onVisible = () => {
      if (document.visibilityState === "visible") handleReconnect();
    };
    const timer = setInterval(onRetry, LOST_AUTO_RECONNECT_INTERVAL_MS);
    window.addEventListener("online", onRetry);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      window.removeEventListener("online", onRetry);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [state, handleReconnect]);

  // Focus-driven audio: in embed mode we auto-unmute while this iframe
  // has focus and mute as soon as focus leaves. This matches the UX
  // a user expects from an embedded "phone panel" — hear the device
  // while interacting with it, go silent the moment attention moves
  // back to the surrounding page. Browsers deliver window focus/blur
  // events to the iframe's own window even across origins, so this
  // works regardless of whether the parent page is same-origin.
  //
  // Autoplay policy note: videos start muted (isMuted=true), which
  // satisfies "autoplay muted is always allowed". The very first
  // pointerdown inside the iframe constitutes a user gesture that
  // permits subsequent programmatic unmuting for the session.
  useEffect(() => {
    if (!embed) return;

    const handleFocus = () => setIsMuted(false);
    const handleBlur = () => setIsMuted(true);
    // Safety net: some browsers don't fire window.focus on the very
    // first click into the iframe if the parent page had focus on
    // a non-iframe element — rely on pointerdown to force unmute.
    const handlePointerDown = () => setIsMuted(false);

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("pointerdown", handlePointerDown);

    // If the iframe already has focus at mount (e.g. user navigated
    // directly), start unmuted.
    if (document.hasFocus()) setIsMuted(false);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [embed]);

  // Auto-start capture because an in-guest app opened the camera. Unlike the
  // manual toggle this may be denied without a user gesture (Safari), so a
  // denial is remembered for the edge and surfaced to the parent window.
  const autoStartCamera = useCallback(async () => {
    const handle = viewerRef.current;
    if (!handle || handle.isCameraSharing()) return;
    setCameraBusy(true);
    try {
      await handle.startCamera("front");
      setCameraSharing(true);
      postStatus("camera-status", { active: true });
    } catch (err) {
      console.warn("[App] auto camera start failed (permission?):", err);
      cameraDeniedRef.current = true;
      setCameraSharing(false);
      setCameraBusy(false);
      // Parent (PhoneSidebar) shows a "click to allow camera" hint — a
      // user-gesture retry path for browsers that reject gestureless
      // getUserMedia (Safari) or when the user dismissed the prompt.
      postStatus("camera-permission-denied");
    }
  }, []);

  const handleDataChannelMessage = useCallback((msg: DataChannelMessage) => {
    if (msg.type === "device_info") {
      const d = (msg as DeviceInfoMessage).device;
      const [w, h] = (d.resolution ?? "1080x1920").split("x").map(Number);
      const info = { name: d.name, os: d.os, width: w || 1080, height: h || 1920 };
      setDeviceInfo(info);
      postStatus("device-info", { device: info });
    } else if (msg.type === "camera_status") {
      // Bridge acknowledged (or reported failure of) the virtual-camera
      // wiring. Sync the toggle to the authoritative device-side state.
      setCameraSharing(msg.active);
      setCameraBusy(false);
      postStatus("camera-status", { active: msg.active, reason: msg.reason });
    } else if (msg.type === "camera_needed") {
      // Device-driven auto share: an in-guest app opened/closed the camera.
      // Mirror the in-use edge to the parent window so its camera indicator
      // can stay visible (as a re-enable toggle) while the device camera is
      // open even after the user manually stopped sharing.
      postStatus("camera-needed", { active: msg.active });
      if (msg.active) {
        // Cancel any pending debounced stop — lens switches emit
        // close→open within ~1s and must not flicker the capture.
        if (cameraStopDebounceRef.current) {
          clearTimeout(cameraStopDebounceRef.current);
          cameraStopDebounceRef.current = null;
        }
        // Within one in-use edge, respect an earlier denial or an explicit
        // manual stop (bridge replays the flag on reconnect — that must
        // not override user intent).
        if (cameraDeniedRef.current || cameraManualStopRef.current) return;
        void autoStartCamera();
      } else {
        // Edge over: clear per-edge memory so the NEXT app open prompts
        // again / auto-starts again.
        cameraDeniedRef.current = false;
        cameraManualStopRef.current = false;
        if (cameraStopDebounceRef.current) clearTimeout(cameraStopDebounceRef.current);
        cameraStopDebounceRef.current = setTimeout(() => {
          cameraStopDebounceRef.current = null;
          const handle = viewerRef.current;
          if (handle?.isCameraSharing()) {
            void handle.stopCamera().then(() => {
              setCameraSharing(false);
              postStatus("camera-status", { active: false, reason: "device camera closed" });
            }).catch(() => {});
          }
        }, CAMERA_STOP_DEBOUNCE_MS);
      }
    }
  }, [autoStartCamera]);

  const handleVideoDimensions = useCallback((width: number, height: number) => {
    setDeviceInfo((current) => ({
      name: current?.name ?? "Android device",
      os: current?.os ?? "Android",
      width,
      height,
    }));
    postStatus("device-info", { device: { width, height } });
  }, []);

  const toggleCamera = useCallback(async () => {
    const handle = viewerRef.current;
    if (!handle || cameraBusy) return;
    setCameraBusy(true);
    try {
      if (handle.isCameraSharing()) {
        // Explicit user stop: remember the intent for the rest of this
        // in-use edge so camera_needed replays don't restart capture.
        cameraManualStopRef.current = true;
        await handle.stopCamera();
        setCameraSharing(false);
        setCameraBusy(false);
        // Tell the parent directly — the bridge does not ack camera_stop,
        // and the parent's indicator must flip to its "disabled" state
        // (not disappear) so the user can re-enable sharing.
        postStatus("camera-status", { active: false, reason: "manual stop" });
      } else {
        // Manual (re)start is a user gesture — it overrides an earlier
        // denial or manual stop for this edge.
        cameraDeniedRef.current = false;
        cameraManualStopRef.current = false;
        // Optimistically flip to sharing; the bridge's camera_status
        // ack confirms (or the catch below rolls back). Keep busy until
        // the ack lands so double-clicks can't race the renegotiation.
        await handle.startCamera("front");
        setCameraSharing(true);
        postStatus("camera-status", { active: true });
      }
    } catch (err) {
      console.error("[App] camera toggle failed:", err);
      setCameraSharing(false);
      setCameraBusy(false);
    }
  }, [cameraBusy]);

  // Embedded mode hides the toolbar, so the camera toggle must be drivable
  // by the parent window (PhoneSidebar / DesktopSidebar): listen for
  // `beeos.viewer.camera.toggle` postMessages. State flows back via the
  // existing postStatus("camera-status") emission in handleDataChannelMessage.
  useEffect(() => {
    if (!embed) return;
    function onParentMessage(e: MessageEvent) {
      const m = e.data;
      if (!m || typeof m !== "object") return;
      if (m.type === "beeos.viewer.camera.toggle") {
        void toggleCamera();
        return;
      }
      if (m.type !== "beeos.viewer.system-button") return;
      switch (m.button) {
        case "back":
          viewerRef.current?.sendControl({ type: "back" });
          break;
        case "home":
          viewerRef.current?.sendControl({ type: "home" });
          break;
        case "recents":
          viewerRef.current?.sendControl({
            type: "key",
            action: "down",
            keycode: "KEYCODE_APP_SWITCH",
          });
          break;
      }
    }
    window.addEventListener("message", onParentMessage);
    return () => window.removeEventListener("message", onParentMessage);
  }, [embed, toggleCamera]);

  const sendControl = useCallback((msg: DataChannelMessage) => {
    viewerRef.current?.sendControl(msg);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  if (state.phase === "error") {
    postStatus("error", { kind: state.kind, message: state.message });
    return (
      <div className="flex h-screen items-center justify-center bg-black px-4 py-6">
        <div className="w-full max-w-sm">
          <ErrorOverlay
            kind={state.kind}
            message={state.message}
            canReconnect={state.streamParams !== undefined}
            compact={embed}
            onReconnect={handleReconnect}
          />
        </div>
      </div>
    );
  }

  if (state.phase === "loading") {
    return (
      <div className="flex h-screen items-center justify-center bg-black px-4 py-6">
        <div className="w-full max-w-sm">
          <ViewerOverlay
            tone="info"
            icon={<ViewerSpinner compact={embed} />}
            title={t("viewer.connectingToDevice")}
            description={t("viewer.connectingToDeviceDescription")}
            compact={embed}
          />
        </div>
      </div>
    );
  }

  const streamParams = isLocalViewerHostname(window.location.hostname)
    ? { ...state.streamParams, iceTransportPolicy: "all" as const }
    : state.streamParams;
  const showToolbar = !embed;

  if (mode === "desktop") {
    const desktopParams = toDesktopStreamParams(streamParams);
    return (
      <DesktopView
        streamParams={desktopParams}
        showToolbar={showToolbar}
        onConnected={() => { setConnected(true); postStatus("connected"); }}
        onDisconnected={() => { setConnected(false); postStatus("disconnected"); }}
        connected={connected}
        refreshStreamParams={desktopRefresher}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex h-screen flex-col overflow-hidden bg-black"
    >
      {showToolbar && (
        <div
          className="flex items-center justify-between px-4 py-2"
          style={{ background: "var(--color-surface)", borderBottom: "1px solid var(--color-border)" }}
        >
          <div className="flex items-center gap-3">
            <StatusDot connected={connected} />
            {deviceInfo && (
              <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                {deviceInfo.name} &middot; {deviceInfo.os} &middot; {deviceInfo.width}&times;{deviceInfo.height}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1">
            <ToolbarButton
              label={cameraSharing ? t("viewer.stopCamera") : t("viewer.shareCamera")}
              onClick={toggleCamera}
              active={cameraSharing}
              disabled={cameraBusy}
            >
              {cameraSharing ? <CameraIcon /> : <CameraOffIcon />}
            </ToolbarButton>
            <ToolbarButton label={isMuted ? t("viewer.unmute") : t("viewer.mute")} onClick={() => setIsMuted((m) => !m)}>
              {isMuted ? <MutedIcon /> : <UnmutedIcon />}
            </ToolbarButton>
            <ToolbarButton label={isFullscreen ? t("viewer.exitFullscreen") : t("viewer.fullscreen")} onClick={toggleFullscreen}>
              <FullscreenIcon />
            </ToolbarButton>
          </div>
        </div>
      )}

      {/* Device viewer. Embed is already a 9:16 iframe — stretch to fill.
          Centering + object-fit:contain in a non-9:16 flex pane is what
          painted the top/bottom black bars in the workspace Phone panel. */}
      <div
        className={
          embed
            ? "min-h-0 flex-1 overflow-hidden bg-black"
            : "flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-black"
        }
      >
        <DeviceViewer
          key={remountKey}
          ref={viewerRef}
          streamParams={streamParams}
          deviceWidth={deviceInfo?.width ?? 1080}
          deviceHeight={deviceInfo?.height ?? 1920}
          muted={isMuted}
          style={{ width: "100%", height: "100%", display: "block" }}
          onConnected={handleConnected}
          onDisconnected={handleDisconnected}
          onReconnecting={handleReconnecting}
          onDataChannelMessage={handleDataChannelMessage}
          onVideoDimensions={handleVideoDimensions}
          refreshStreamParams={
            desktopRefresher
              ? async () => {
                  // `desktopRefresher` returns DesktopStreamParams
                  // (renamed shape); WebRTCClient on the device branch
                  // expects the original `StreamParams`. The fields
                  // used by the device branch are structurally
                  // compatible, including the parent-mediated
                  // url-streamParams path.
                  const next = (await desktopRefresher()) as unknown as StreamParams;
                  return next;
                }
              : undefined
          }
        />
      </div>

      {/* Extra viewer chrome. The encoded stream already includes Android's
          system nav; drawing a second bar inside a 9:16 embed makes the
          video pane shorter than 9:16 and the phone looks stretched. */}
      {!embed && (
        <div
          className="flex items-center justify-center gap-12 py-2 shrink-0"
          style={{ background: "#111" }}
        >
          <NavButton label={t("viewer.back")} onClick={() => sendControl({ type: "back" })}>
            <BackTriangleIcon />
          </NavButton>
          <NavButton label={t("viewer.home")} onClick={() => sendControl({ type: "home" })}>
            <HomeCircleIcon />
          </NavButton>
          <NavButton
            label={t("viewer.recents")}
            onClick={() => sendControl({ type: "key", action: "down", keycode: "KEYCODE_APP_SWITCH" })}
          >
            <RecentsSquareIcon />
          </NavButton>
        </div>
      )}
    </div>
  );
}

function DesktopView({
  streamParams,
  showToolbar,
  onConnected,
  onDisconnected,
  refreshStreamParams,
}: {
  streamParams: DesktopStreamParams;
  showToolbar: boolean;
  onConnected: () => void;
  onDisconnected: () => void;
  connected: boolean;
  /**
   * Forwarded to `useDesktopViewer`. When set, the underlying
   * `DesktopViewer` arms a pre-emptive timer that fires ~60s before
   * `streamParams.expiresAt` and calls this fn to rotate the JWT
   * before EMQX has a chance to reject the next reconnect with
   * "Bad User Name or Password". Also called on user-initiated
   * `retry()` so a stale token doesn't lock the user out indefinitely.
   */
  refreshStreamParams?: RefreshStreamParamsFn;
}) {
  const { t } = useTranslation();
  const { containerRef, state, errorMessage, connect, disconnect, retry } = useDesktopViewer({
    streamParams,
    refreshStreamParams,
    onConnected,
    onDisconnected,
    onError: (err) => console.error("Desktop viewer error:", err),
  });

  const mounted = useRef(false);
  useEffect(() => {
    // StrictMode: mount(1) → unmount(1) → mount(2).
    // We connect on mount(2) — the real one that sticks.
    mounted.current = true;
    const timer = setTimeout(() => {
      if (mounted.current) connect();
    }, 0);
    return () => {
      mounted.current = false;
      clearTimeout(timer);
      disconnect();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetry = useCallback(() => {
    // Route through `retry()` so the next attempt picks up a freshly
    // rotated MQTT JWT (via `refreshStreamParams`) before re-handshaking
    // with EMQX. Old behaviour (`disconnect+connect`) re-used the
    // expired token and caused the "Bad User Name or Password" loop.
    retry();
  }, [retry]);

  const isConnected = state === "connected";

  return (
    <div className="flex flex-col h-screen">
      {showToolbar && (
        <div
          className="flex items-center justify-between px-4 py-2 shrink-0"
          style={{ background: "var(--color-surface)", borderBottom: "1px solid var(--color-border)" }}
        >
          <StatusDot connected={isConnected} />
          <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
            {t("viewer.desktop", { state })}
          </span>
        </div>
      )}
      <div className="flex-1 relative bg-black">
        <div ref={containerRef} className="absolute inset-0" />

        {(state === "connecting" || state === "failed" || state === "disconnected") && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/70 px-6 py-10 backdrop-blur-md">
            <div className="w-full max-w-2xl">
              {state === "connecting" && (
                <ViewerOverlay
                  tone="info"
                  icon={<ViewerSpinner />}
                  title={t("viewer.connectingToDesktop")}
                  description={t("viewer.connectingDescription")}
                />
              )}
              {state === "failed" && (
                <ViewerOverlay
                  tone="error"
                  icon={<AlertCircleIcon className="text-[var(--color-error)]" />}
                  title={t("viewer.connectionFailed")}
                  description={errorMessage || t("viewer.connectionFailedDescription")}
                  action={
                    <ViewerPrimaryButton onClick={handleRetry}>
                      <RefreshCwIcon />
                      {t("viewer.retry")}
                    </ViewerPrimaryButton>
                  }
                />
              )}
              {state === "disconnected" && mounted.current && (
                <ViewerOverlay
                  tone="warning"
                  icon={<WifiOffIcon className="text-[var(--color-warning)]" />}
                  title={t("viewer.disconnected")}
                  description={t("viewer.disconnectedDescription")}
                  action={
                    <ViewerPrimaryButton onClick={handleRetry}>
                      <RefreshCwIcon />
                      {t("viewer.reconnect")}
                    </ViewerPrimaryButton>
                  }
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Top-level error overlay dispatcher. Maps `ErrorKind` to a tone /
 * icon / title / description / optional reconnect CTA, all sourced
 * from `<ViewerOverlay>` so the device branch shares one visual
 * language with the desktop branch's connection-failed / disconnected
 * cards.
 *
 * Reconnect CTA is only shown when the iframe has cached
 * `streamParams` to replay (`canReconnect`); the unrecoverable
 * "fatal" kind never gets a button because the user can't fix
 * missing-token / unauthorized issues from inside the viewer.
 */
function ErrorOverlay({
  kind,
  message,
  canReconnect,
  compact,
  onReconnect,
}: {
  kind: ErrorKind;
  message?: string;
  canReconnect: boolean;
  compact: boolean;
  onReconnect: () => void;
}) {
  const { t } = useTranslation();
  const reconnectButton =
    canReconnect && kind !== "fatal" ? (
      <ViewerPrimaryButton onClick={onReconnect} compact={compact}>
        <RefreshCwIcon />
        {kind === "kicked" ? t("viewer.takeOver") : t("viewer.reconnect")}
      </ViewerPrimaryButton>
    ) : undefined;

  if (kind === "kicked") {
    return (
      <ViewerOverlay
        tone="warning"
        compact={compact}
        icon={<ScreenShareTakeoverIcon className="text-[var(--color-warning)]" />}
        title={t("viewer.viewerKickedTitle")}
        description={message || t("viewer.viewerKickedDescription")}
        action={reconnectButton}
      />
    );
  }

  if (kind === "lost") {
    return (
      <ViewerOverlay
        tone="error"
        compact={compact}
        icon={<WifiOffIcon className="text-[var(--color-error)]" />}
        title={t("viewer.connectionLostTitle")}
        description={message || t("viewer.connectionLostDescription")}
        action={reconnectButton}
      />
    );
  }

  return (
    <ViewerOverlay
      tone="error"
      compact={compact}
      icon={<AlertCircleIcon className="text-[var(--color-error)]" />}
      title={t("viewer.unableToConnect")}
      description={message}
    />
  );
}

function StatusDot({ connected }: { connected: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5">
      <div
        className="w-2 h-2 rounded-full"
        style={{ background: connected ? "var(--color-success)" : "var(--color-error)" }}
      />
      <span className="text-xs font-medium" style={{ color: connected ? "var(--color-success)" : "var(--color-text-muted)" }}>
        {connected ? t("viewer.connected") : t("viewer.connecting")}
      </span>
    </div>
  );
}

function ToolbarButton({
  label,
  onClick,
  children,
  active = false,
  disabled = false,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors"
      style={{
        color: active ? "var(--color-primary, #3b82f6)" : "var(--color-text-muted)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-border)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {children}
    </button>
  );
}

function NavButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      title={label}
      onClick={onClick}
      className="inline-flex items-center justify-center w-12 h-10 rounded-lg transition-colors cursor-pointer"
      style={{ color: "#888" }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "#ccc"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#888"; }}
    >
      {children}
    </button>
  );
}

function BackTriangleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="15,4 3,12 15,20" />
    </svg>
  );
}

function HomeCircleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

function RecentsSquareIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="14" height="14" x="5" y="5" rx="1" />
    </svg>
  );
}

function MutedIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 5 6 9H2v6h4l5 4V5Z" />
      <line x1="23" y1="9" x2="17" y2="15" />
      <line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  );
}

function UnmutedIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 5 6 9H2v6h4l5 4V5Z" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  );
}

function CameraOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
      <circle cx="12" cy="13" r="3" />
      <line x1="3" y1="3" x2="21" y2="21" />
    </svg>
  );
}

function FullscreenIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" />
      <path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}
